import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { STATUS_OPTIONS } from "@/components/StatusBadge";
import StatusSelect from "@/components/StatusSelect";
import { useLiveRefresh } from "@/contexts/LiveRefresh";
import TaskFormDialog from "@/components/TaskFormDialog";
import TaskDetailDialog from "@/components/TaskDetailDialog";
import {
    Plus,
    Search,
    Loader2,
    BadgeIndianRupee,
    Repeat,
    X,
    FileSpreadsheet,
    ChevronDown,
    ChevronUp,
    SlidersHorizontal,
    Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { API } from "@/lib/api";

const PRIORITY_BADGE = {
    high: "bg-red-50 text-red-700 border-red-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    low: "bg-slate-100 text-slate-700 border-slate-200",
};

// Default ordering when no column sort is chosen (not alphabetical).
const STATUS_RANK = {
    in_progress: 0,
    not_initiated: 1,
    pending_from_client: 2,
    pending_from_department: 3,
    completed: 4,
};

const PRIORITY_OPTIONS = [
    { value: "high", label: "High" },
    { value: "medium", label: "Medium" },
    { value: "low", label: "Low" },
];

// Fields available in the ServiceNow-style custom filter builder.
const CUSTOM_FIELDS = [
    { key: "client_name", label: "Client", type: "text" },
    { key: "work_type", label: "Type of work", type: "text" },
    { key: "assigned_to_name", label: "Assigned to", type: "text" },
    { key: "status", label: "Status", type: "enum", options: STATUS_OPTIONS },
    { key: "priority", label: "Priority", type: "enum", options: PRIORITY_OPTIONS },
    { key: "task_date", label: "Start date", type: "date" },
    { key: "deadline_date", label: "Due date", type: "date" },
];
const OPS_BY_TYPE = {
    text: [
        { value: "contains", label: "contains" },
        { value: "is", label: "is" },
        { value: "is_not", label: "is not" },
    ],
    enum: [
        { value: "is", label: "is" },
        { value: "is_not", label: "is not" },
    ],
    date: [
        { value: "on", label: "on" },
        { value: "before", label: "before" },
        { value: "after", label: "after" },
    ],
};

function fieldDef(key) {
    return CUSTOM_FIELDS.find((f) => f.key === key) || CUSTOM_FIELDS[0];
}

function matchCondition(task, c) {
    const def = fieldDef(c.field);
    const raw = task[c.field];
    if (def.type === "date") {
        if (!c.value) return true;
        const d = raw || "";
        if (!d) return false;
        if (c.op === "on") return d === c.value;
        if (c.op === "before") return d < c.value;
        if (c.op === "after") return d > c.value;
        return true;
    }
    const s = String(raw ?? "").toLowerCase();
    const q = String(c.value ?? "").toLowerCase();
    if (c.op === "contains") return s.includes(q);
    if (c.op === "is") return s === q;
    if (c.op === "is_not") return s !== q;
    return true;
}

function formatDate(d) {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return d;
    }
}

function isOverdue(deadline, status) {
    if (!deadline || status === "completed") return false;
    return deadline < new Date().toISOString().slice(0, 10);
}

// Inline reimbursable-amount editor for the Tasks table. Saves on blur / Enter.
function ReimbAmount({ task, onSave }) {
    const [val, setVal] = useState(task.reimbursable_amount ?? "");
    useEffect(() => {
        setVal(task.reimbursable_amount ?? "");
    }, [task.reimbursable_amount]);
    const commit = () => {
        const next = val === "" ? null : Number(val);
        if (next !== (task.reimbursable_amount ?? null)) onSave(next);
    };
    return (
        <input
            type="number"
            min="0"
            step="0.01"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
            }}
            onClick={(e) => e.stopPropagation()}
            placeholder="₹ amt"
            className="mt-1 w-20 rounded border border-border px-1.5 py-0.5 text-center text-xs focus:border-primary focus:outline-none"
            data-testid={`reimb-amount-${task.id}`}
        />
    );
}

export default function Tasks() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [detailTask, setDetailTask] = useState(null);
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState("asc");
    const [selected, setSelected] = useState(() => new Set());

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [billingFilter, setBillingFilter] = useState("all");
    const [assignedFilter, setAssignedFilter] = useState("all");
    const [users, setUsers] = useState([]);

    // Advanced filters
    const [clients, setClients] = useState([]);
    const [clientFilter, setClientFilter] = useState("all");
    const [workTypeFilter, setWorkTypeFilter] = useState("all");
    const [dueOp, setDueOp] = useState("any"); // any | before | on | after
    const [dueDate, setDueDate] = useState("");
    const [overdueOnly, setOverdueOnly] = useState(false);
    const [showCustom, setShowCustom] = useState(false);
    const [conditions, setConditions] = useState([]); // ServiceNow-style builder
    const [showCompleted, setShowCompleted] = useState(false);

    const presetFilter = searchParams.get("filter");

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter !== "all") params.status = statusFilter;
            if (priorityFilter !== "all") params.priority = priorityFilter;
            if (assignedFilter !== "all") params.assigned_to = assignedFilter;
            if (billingFilter === "billed") params.billing_status = true;
            if (billingFilter === "unbilled") params.billing_status = false;
            if (search.trim()) params.search = search.trim();
            const { data } = await api.get("/tasks", { params });
            setTasks(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, priorityFilter, billingFilter, assignedFilter]);
    useLiveRefresh(load);

    const workTypes = useMemo(
        () => [...new Set(tasks.map((t) => t.work_type).filter(Boolean))].sort(),
        [tasks],
    );

    const filtered = useMemo(() => {
        let list = tasks;
        if (presetFilter === "unbilled") {
            list = list.filter((t) => t.status === "completed" && !t.billing_status);
        } else if (presetFilter === "reimbursable") {
            list = list.filter((t) => t.reimbursable_expense && !t.billing_status);
        } else if (presetFilter === "overdue") {
            list = list.filter((t) => isOverdue(t.deadline_date, t.status));
        }
        if (clientFilter !== "all") list = list.filter((t) => t.client_name === clientFilter);
        if (workTypeFilter !== "all") list = list.filter((t) => t.work_type === workTypeFilter);
        if (overdueOnly) list = list.filter((t) => isOverdue(t.deadline_date, t.status));
        if (dueOp !== "any" && dueDate) {
            list = list.filter((t) => {
                const d = t.deadline_date || "";
                if (!d) return false;
                if (dueOp === "on") return d === dueDate;
                if (dueOp === "before") return d < dueDate;
                if (dueOp === "after") return d > dueDate;
                return true;
            });
        }
        const active = conditions.filter((c) => c.field && c.op);
        if (active.length) list = list.filter((t) => active.every((c) => matchCondition(t, c)));
        return list;
    }, [tasks, presetFilter, clientFilter, workTypeFilter, overdueOnly, dueOp, dueDate, conditions]);

    const sorted = useMemo(() => {
        if (!sortKey) {
            // Default: fixed status order, newest first within each status.
            return [...filtered].sort((a, b) => {
                const ra = STATUS_RANK[a.status] ?? 99;
                const rb = STATUS_RANK[b.status] ?? 99;
                if (ra !== rb) return ra - rb;
                return (b.created_at || "").localeCompare(a.created_at || "");
            });
        }
        const dir = sortDir === "asc" ? 1 : -1;
        const PRIO = { high: 3, medium: 2, low: 1 };
        const keyVal = (t) => {
            switch (sortKey) {
                case "client": return (t.client_name || "").toLowerCase();
                case "work": return (t.work_type || "").toLowerCase();
                case "assigned": return (t.assigned_to_name || "").toLowerCase();
                case "status": return STATUS_RANK[t.status] ?? 99;
                case "priority": return PRIO[t.priority] || 0;
                default: return t[sortKey] || ""; // task_date, deadline_date
            }
        };
        return [...filtered].sort((a, b) => {
            const va = keyVal(a), vb = keyVal(b);
            if (va < vb) return -dir;
            if (va > vb) return dir;
            return 0;
        });
    }, [filtered, sortKey, sortDir]);

    const toggleSort = (key) => {
        if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(key); setSortDir("asc"); }
    };

    const visibleIds = sorted.map((t) => t.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
    const toggleAll = () =>
        setSelected(allSelected ? new Set() : new Set(visibleIds));
    const toggleOne = (id) =>
        setSelected((s) => {
            const n = new Set(s);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });

    const bulkUpdate = async (patch) => {
        const ids = [...selected];
        if (!ids.length) return;
        try {
            for (const id of ids) await api.patch(`/tasks/${id}`, patch);
            toast.success(`Updated ${ids.length} task${ids.length > 1 ? "s" : ""}`);
            setSelected(new Set());
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const sortHead = (k, label, extraClass = "") => (
        <TableHead
            className={`cursor-pointer select-none ${extraClass}`}
            onClick={() => toggleSort(k)}
            data-testid={`sort-${k}`}
        >
            <span className="inline-flex items-center gap-1">
                {label}
                {sortKey === k ? (
                    sortDir === "asc" ? (
                        <ChevronUp className="h-3 w-3" />
                    ) : (
                        <ChevronDown className="h-3 w-3" />
                    )
                ) : null}
            </span>
        </TableHead>
    );

    const toggleField = async (task, field, value) => {
        try {
            await api.patch(`/tasks/${task.id}`, { [field]: value });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    // --- Custom filter builder helpers ---
    const addCondition = () =>
        setConditions((cs) => [
            ...cs,
            { id: `${cs.length}-${cs.reduce((m, c) => m + c.id.length, 1)}`, field: "client_name", op: "contains", value: "" },
        ]);
    const updateCondition = (id, patch) =>
        setConditions((cs) =>
            cs.map((c) => {
                if (c.id !== id) return c;
                const next = { ...c, ...patch };
                if (patch.field) {
                    // Reset op/value to something valid for the new field type.
                    const def = fieldDef(patch.field);
                    next.op = OPS_BY_TYPE[def.type][0].value;
                    next.value = "";
                }
                return next;
            }),
        );
    const removeCondition = (id) =>
        setConditions((cs) => cs.filter((c) => c.id !== id));

    const advancedActive =
        clientFilter !== "all" ||
        workTypeFilter !== "all" ||
        overdueOnly ||
        (dueOp !== "any" && dueDate) ||
        conditions.some((c) => c.value);
    const clearAdvanced = () => {
        setClientFilter("all");
        setWorkTypeFilter("all");
        setOverdueOnly(false);
        setDueOp("any");
        setDueDate("");
        setConditions([]);
    };

    const exportTasks = (format) => {
        const token = localStorage.getItem("sdc_token");
        const params = new URLSearchParams({ format, auth: token || "" });
        if (statusFilter !== "all") params.set("status", statusFilter);
        if (priorityFilter !== "all") params.set("priority", priorityFilter);
        if (assignedFilter !== "all") params.set("assigned_to", assignedFilter);
        if (billingFilter === "billed") params.set("billing_status", "true");
        if (billingFilter === "unbilled") params.set("billing_status", "false");
        if (search.trim()) params.set("search", search.trim());
        window.location.href = `${API}/exports/tasks?${params.toString()}`;
    };

    // Completed tasks are tucked into a collapsible group to reduce clutter.
    const activeRows = sorted.filter((t) => t.status !== "completed");
    const completedRows = sorted.filter((t) => t.status === "completed");
    const expandCompleted = showCompleted || statusFilter === "completed";

    const renderRow = (t) => (
        <TableRow
            key={t.id}
            className="sdc-table-row cursor-pointer"
            data-testid={`task-row-${t.id}`}
            onClick={() => setDetailTask(t)}
        >
            <TableCell onClick={(e) => e.stopPropagation()}>
                <Checkbox
                    checked={selected.has(t.id)}
                    onCheckedChange={() => toggleOne(t.id)}
                    data-testid={`select-${t.id}`}
                />
            </TableCell>
            <TableCell>
                <div className="font-semibold text-foreground">{t.client_name}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1">
                    {t.reimbursable_expense && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-primary">
                            <BadgeIndianRupee className="h-3 w-3" />
                            Reimbursable
                        </span>
                    )}
                    {t.recurrence && t.recurrence !== "none" && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-700">
                            <Repeat className="h-3 w-3" />
                            {t.recurrence}
                        </span>
                    )}
                </div>
            </TableCell>
            <TableCell>{t.work_type}</TableCell>
            <TableCell className="text-sm">
                {t.assigned_to_name || <span className="text-muted-foreground">—</span>}
            </TableCell>
            <TableCell className="text-sm">{formatDate(t.task_date)}</TableCell>
            <TableCell className="text-sm">
                {t.deadline_date ? (
                    <span
                        className={
                            isOverdue(t.deadline_date, t.status)
                                ? "text-red-700 font-semibold"
                                : ""
                        }
                    >
                        {formatDate(t.deadline_date)}
                    </span>
                ) : (
                    <span className="text-muted-foreground">—</span>
                )}
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
                <StatusSelect value={t.status} onChange={(v) => toggleField(t, "status", v)} />
            </TableCell>
            <TableCell>
                <span
                    className={`sdc-pill border ${PRIORITY_BADGE[t.priority] || PRIORITY_BADGE.medium}`}
                >
                    {t.priority?.toUpperCase()}
                </span>
            </TableCell>
            <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-col items-center gap-1">
                    <Checkbox
                        checked={!!t.reimbursable_expense}
                        onCheckedChange={(v) => toggleField(t, "reimbursable_expense", !!v)}
                        data-testid={`toggle-reimb-${t.id}`}
                    />
                    {t.reimbursable_expense && (
                        <ReimbAmount
                            task={t}
                            onSave={(n) => toggleField(t, "reimbursable_amount", n)}
                        />
                    )}
                </div>
            </TableCell>
            <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                    checked={!!t.approved}
                    onCheckedChange={(v) => toggleField(t, "approved", !!v)}
                    data-testid={`toggle-approved-${t.id}`}
                />
            </TableCell>
        </TableRow>
    );

    return (
        <div className="space-y-6" data-testid="tasks-page">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        Engagements
                    </div>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                        Task manager
                    </h1>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" data-testid="tasks-export-trigger">
                                <FileSpreadsheet className="mr-1.5 h-4 w-4" />
                                Export
                                <ChevronDown className="ml-1 h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onClick={() => exportTasks("xlsx")}
                                data-testid="tasks-export-xlsx"
                            >
                                Excel (.xlsx)
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => exportTasks("csv")}
                                data-testid="tasks-export-csv"
                            >
                                CSV (.csv)
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-task-button">
                        <Plus className="mr-1.5 h-4 w-4" />
                        New task
                    </Button>
                </div>
            </div>

            {/* Preset filter banner */}
            {presetFilter && (
                <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5 text-sm">
                    <div className="text-primary font-medium">
                        Showing{" "}
                        <span className="font-semibold">
                            {presetFilter === "unbilled"
                                ? "Completed but unbilled"
                                : presetFilter === "reimbursable"
                                  ? "Pending reimbursable"
                                  : "Overdue"}
                        </span>{" "}
                        tasks
                    </div>
                    <button
                        className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                        onClick={() => setSearchParams({})}
                        data-testid="clear-preset-filter"
                    >
                        <X className="h-3.5 w-3.5" /> Clear filter
                    </button>
                </div>
            )}

            {/* Filters */}
            <Card className="sdc-card p-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
                    <div className="md:col-span-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search client, work, comments..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && load()}
                                className="pl-9"
                                data-testid="task-search-input"
                            />
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <Select value={assignedFilter} onValueChange={setAssignedFilter}>
                            <SelectTrigger data-testid="filter-assigned-trigger">
                                <SelectValue placeholder="Assigned to" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All assignees</SelectItem>
                                {users.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>
                                        {u.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-2">
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger data-testid="filter-status-trigger">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All statuses</SelectItem>
                                {STATUS_OPTIONS.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                        {s.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-2">
                        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                            <SelectTrigger data-testid="filter-priority-trigger">
                                <SelectValue placeholder="Priority" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All priorities</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="low">Low</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-2">
                        <Select value={billingFilter} onValueChange={setBillingFilter}>
                            <SelectTrigger data-testid="filter-billing-trigger">
                                <SelectValue placeholder="Billing" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All bills</SelectItem>
                                <SelectItem value="billed">Billed</SelectItem>
                                <SelectItem value="unbilled">Unbilled</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-1">
                        <Button variant="secondary" className="w-full" onClick={load} data-testid="filter-apply">
                            Search
                        </Button>
                    </div>
                </div>

                {/* Advanced filters: client, type of work, due date, overdue */}
                <div className="mt-3 grid grid-cols-1 gap-3 border-t border-border pt-3 md:grid-cols-12">
                    <div className="md:col-span-3">
                        <Select value={clientFilter} onValueChange={setClientFilter}>
                            <SelectTrigger data-testid="filter-client-trigger">
                                <SelectValue placeholder="Client" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All clients</SelectItem>
                                {clients.map((c) => (
                                    <SelectItem key={c.id} value={c.name}>
                                        {c.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-3">
                        <Select value={workTypeFilter} onValueChange={setWorkTypeFilter}>
                            <SelectTrigger data-testid="filter-worktype-trigger">
                                <SelectValue placeholder="Type of work" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All work types</SelectItem>
                                {workTypes.map((w) => (
                                    <SelectItem key={w} value={w}>
                                        {w}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-2">
                        <Select value={dueOp} onValueChange={setDueOp}>
                            <SelectTrigger data-testid="filter-due-op-trigger">
                                <SelectValue placeholder="Due date" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">Due date: any</SelectItem>
                                <SelectItem value="before">Due before</SelectItem>
                                <SelectItem value="on">Due on</SelectItem>
                                <SelectItem value="after">Due after</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-2">
                        <Input
                            type="date"
                            value={dueDate}
                            disabled={dueOp === "any"}
                            onChange={(e) => setDueDate(e.target.value)}
                            data-testid="filter-due-date"
                        />
                    </div>
                    <div className="flex items-center gap-2 md:col-span-2">
                        <label className="flex cursor-pointer items-center gap-2 text-sm">
                            <Checkbox
                                checked={overdueOnly}
                                onCheckedChange={(v) => setOverdueOnly(!!v)}
                                data-testid="filter-overdue"
                            />
                            Overdue only
                        </label>
                    </div>
                </div>

                {/* Custom (ServiceNow-style) condition builder */}
                <div className="mt-3 border-t border-border pt-3">
                    <div className="flex items-center justify-between">
                        <button
                            type="button"
                            onClick={() => setShowCustom((s) => !s)}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80"
                            data-testid="toggle-custom-filter"
                        >
                            <SlidersHorizontal className="h-4 w-4" />
                            Custom filter
                            {conditions.length > 0 && (
                                <span className="rounded-full bg-primary/10 px-1.5 text-xs">
                                    {conditions.length}
                                </span>
                            )}
                            {showCustom ? (
                                <ChevronUp className="h-4 w-4" />
                            ) : (
                                <ChevronDown className="h-4 w-4" />
                            )}
                        </button>
                        {advancedActive && (
                            <button
                                type="button"
                                onClick={clearAdvanced}
                                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                                data-testid="clear-advanced"
                            >
                                <X className="h-3.5 w-3.5" /> Clear advanced filters
                            </button>
                        )}
                    </div>

                    {showCustom && (
                        <div className="mt-3 space-y-2" data-testid="custom-filter-panel">
                            {conditions.length === 0 && (
                                <p className="text-xs text-muted-foreground">
                                    Build precise filters — add one or more conditions; rows must
                                    match all of them.
                                </p>
                            )}
                            {conditions.map((c) => {
                                const def = fieldDef(c.field);
                                return (
                                    <div
                                        key={c.id}
                                        className="flex flex-wrap items-center gap-2"
                                        data-testid={`condition-${c.id}`}
                                    >
                                        <Select
                                            value={c.field}
                                            onValueChange={(v) => updateCondition(c.id, { field: v })}
                                        >
                                            <SelectTrigger className="w-[160px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CUSTOM_FIELDS.map((f) => (
                                                    <SelectItem key={f.key} value={f.key}>
                                                        {f.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Select
                                            value={c.op}
                                            onValueChange={(v) => updateCondition(c.id, { op: v })}
                                        >
                                            <SelectTrigger className="w-[130px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {OPS_BY_TYPE[def.type].map((o) => (
                                                    <SelectItem key={o.value} value={o.value}>
                                                        {o.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {def.type === "enum" ? (
                                            <Select
                                                value={c.value}
                                                onValueChange={(v) => updateCondition(c.id, { value: v })}
                                            >
                                                <SelectTrigger className="w-[200px]">
                                                    <SelectValue placeholder="Select value" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {def.options.map((o) => (
                                                        <SelectItem key={o.value} value={o.value}>
                                                            {o.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <Input
                                                type={def.type === "date" ? "date" : "text"}
                                                value={c.value}
                                                placeholder="Value"
                                                onChange={(e) =>
                                                    updateCondition(c.id, { value: e.target.value })
                                                }
                                                className="w-[200px]"
                                            />
                                        )}
                                        <Button
                                            type="button"
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => removeCondition(c.id)}
                                            data-testid={`remove-condition-${c.id}`}
                                        >
                                            <Trash2 className="h-4 w-4 text-red-600" />
                                        </Button>
                                    </div>
                                );
                            })}
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={addCondition}
                                data-testid="add-condition"
                            >
                                <Plus className="mr-1.5 h-4 w-4" /> Add condition
                            </Button>
                        </div>
                    )}
                </div>
            </Card>

            {/* Bulk actions */}
            {selected.size > 0 && (
                <Card className="sdc-card flex flex-wrap items-center gap-3 p-3" data-testid="bulk-bar">
                    <span className="text-sm font-semibold">
                        {selected.size} selected
                    </span>
                    <Select onValueChange={(v) => bulkUpdate({ status: v })}>
                        <SelectTrigger className="h-8 w-[190px]" data-testid="bulk-status">
                            <SelectValue placeholder="Set status…" />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                    {s.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => bulkUpdate({ billing_status: true })} data-testid="bulk-billed">
                        Mark billed
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => bulkUpdate({ billing_status: false })} data-testid="bulk-unbilled">
                        Mark unbilled
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                        Clear
                    </Button>
                </Card>
            )}

            {/* Table */}
            <Card className="sdc-card overflow-hidden">
                {loading ? (
                    <div className="grid place-items-center py-20">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                ) : sorted.length === 0 ? (
                    <div className="py-16 text-center">
                        <p className="text-sm text-muted-foreground">
                            No tasks match the current filters.
                        </p>
                        <Button
                            className="mt-4"
                            onClick={() => { setEditing(null); setShowForm(true); }}
                        >
                            <Plus className="mr-1.5 h-4 w-4" /> Add your first task
                        </Button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                                    <TableHead className="w-10">
                                        <Checkbox
                                            checked={allSelected}
                                            onCheckedChange={toggleAll}
                                            data-testid="select-all"
                                        />
                                    </TableHead>
                                    {sortHead("client", "Client", "min-w-[180px]")}
                                    {sortHead("work", "Type of work")}
                                    {sortHead("assigned", "Assigned to")}
                                    {sortHead("task_date", "Start date")}
                                    {sortHead("deadline_date", "Due date")}
                                    {sortHead("status", "Status")}
                                    {sortHead("priority", "Priority")}
                                    <TableHead className="text-center">Reimb.</TableHead>
                                    <TableHead className="text-center">Approved</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {activeRows.map(renderRow)}
                                {completedRows.length > 0 && (
                                    <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                                        <TableCell colSpan={10} className="py-0">
                                            <button
                                                type="button"
                                                onClick={() => setShowCompleted((s) => !s)}
                                                className="flex w-full items-center gap-2 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground"
                                                data-testid="toggle-completed"
                                            >
                                                {expandCompleted ? (
                                                    <ChevronUp className="h-4 w-4" />
                                                ) : (
                                                    <ChevronDown className="h-4 w-4" />
                                                )}
                                                Completed ({completedRows.length})
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {expandCompleted && completedRows.map(renderRow)}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>

            <TaskFormDialog
                open={showForm}
                onOpenChange={setShowForm}
                task={editing}
                onSaved={load}
            />

            <TaskDetailDialog
                open={!!detailTask}
                onOpenChange={(o) => !o && setDetailTask(null)}
                task={detailTask}
                onChanged={load}
                onEdit={(t) => {
                    setDetailTask(null);
                    setEditing(t);
                    setShowForm(true);
                }}
            />
        </div>
    );
}
